/**
 * Flow Regeneration — rest-phrase injection.
 *
 * When a user is stuck AND playing below the accuracy threshold, an easier
 * "rest phrase" is injected into playback to let them recover. Rest phrases are
 * deliberately inert with respect to the complexity model: they receive no
 * verdict, do not move the band, do not earn or lose chevrons, do not count
 * toward progress, and — critically — do NOT consume a PAD slot. They are
 * interleaved into the render queue but are not members of the assessment
 * stream.
 *
 * Everything here is pure so the injection policy can be tested without a
 * scene. Actually inserting the phrase into the render queue is the
 * controller's job (Phase 2).
 */

import { AssessmentBand, TierConfig } from './complexityModel.types';

/** Floors within this distance count as unchanged for stuck-detection. Floors
 *  are `cai`-sized sums, so this only absorbs binary-float residue. */
const FLOOR_EPSILON = 1e-9;

/**
 * Is the user stuck — has the band floor failed to move?
 *
 * ⚠️ **This is not the oracle's `PLATEAU`.** That variable is an anti-jitter
 * cooldown meaning "the band moved on the previous step", which is the
 * *opposite* of stuck; the engineering spec conflates the two and its rest
 * trigger ("plateaued and wma < 95%") reads as nonsense once decoded. This
 * function implements what the trigger was plainly meant to say.
 *
 * Because the cooldown forces a progressing band into a `move → hold → move`
 * cadence, a window of 2 would flag every climbing user as stuck. The default
 * window of 4 spans at least two opportunities to move.
 *
 * @param bands   Trajectory so far, in assessment order.
 * @param config  Supplies `stuckWindow`.
 * @returns `false` until at least `stuckWindow` phrases exist.
 */
export function isStuck(bands: readonly AssessmentBand[], config: TierConfig): boolean {
  if (config.stuckWindow < 2) throw new Error('TierConfig.stuckWindow must be >= 2');
  if (bands.length < config.stuckWindow) return false;

  const window = bands.slice(-config.stuckWindow);
  const reference = window[0].crMin;
  return window.every((band) => Math.abs(band.crMin - reference) < FLOOR_EPSILON);
}

/** Inputs to the rest-phrase decision, gathered by the controller. */
export interface RestContext {
  /** Trajectory so far, in assessment order. */
  bands: readonly AssessmentBand[];
  /** Live accuracy percentage in [0, 100]. See {@link AccuracySource}. */
  accuracy: number;
  /** Assessment phrases played since the last rest phrase (or since session
   *  start). Rest phrases do not increment this — they are not assessments. */
  assessmentsSinceLastRest: number;
}

/**
 * Should a rest phrase be injected before the next assessment phrase?
 *
 * All three conditions must hold:
 *  1. accuracy is below `restAccuracyThreshold` — at or above it the user is in
 *     flow and must not be interrupted (rest phrases are not chevron-eligible
 *     anyway, so interrupting would cost them progress);
 *  2. the user is stuck (see {@link isStuck});
 *  3. at least `restCadence` assessment phrases have elapsed since the last
 *     rest phrase.
 */
export function shouldInjectRest(context: RestContext, config: TierConfig): boolean {
  if (config.restCadence < 1) throw new Error('TierConfig.restCadence must be >= 1');
  if (context.assessmentsSinceLastRest < 0) {
    throw new Error('assessmentsSinceLastRest must be non-negative');
  }

  if (context.accuracy >= config.restAccuracyThreshold) return false;
  if (context.assessmentsSinceLastRest < config.restCadence) return false;
  return isStuck(context.bands, config);
}

/**
 * The complexity band a rest phrase is drawn from: `restPhraseDepth` below the
 * current floor, clamped at 0, with the usual band width.
 *
 * When `crMin <= restPhraseDepth` the rest band bottoms out at 0.0 and overlaps
 * the reserved bottom-of-tier rudimentary pool — intended per the spec.
 */
export function restBand(
  currentFloor: number,
  config: TierConfig,
): Readonly<{ crMin: number; crMax: number }> {
  if (currentFloor < 0 || currentFloor > 1) throw new Error('currentFloor must be in [0, 1]');

  const crMin = Math.max(currentFloor - config.restPhraseDepth, 0);
  return Object.freeze({ crMin, crMax: Math.min(crMin + config.cvr, 1) });
}
